/**
 * Datos de quien reserva y aceptación de las condiciones de cancelación.
 *
 * ## Accesibilidad
 *
 * Cada campo tiene `<label for>` real, texto de ayuda permanente y, cuando falla, un
 * mensaje asociado por `aria-describedby` y marcado con `aria-invalid`. El mensaje no es
 * un tooltip ni un borde rojo: es texto, porque el color no puede ser el unico portador
 * de la informacion (WCAG 1.4.1 y 3.3.1).
 *
 * ## Privacidad
 *
 * Lo que se escribe o se recupera enmascarado por ADR-066 vive **solo en memoria**. No se guarda en
 * `localStorage`, ni en `sessionStorage`, ni en la URL, ni se registra en la consola. Al
 * confirmar, el formulario se desmonta y el estado desaparece con el
 * (docs/SEGURIDAD.md §6).
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { normalizarCedula, valorCedula } from '@padel/domain';

import {
  FORMULARIO_VACIO,
  LARGOS,
  esquemaFormularioReserva,
  type FormularioReserva,
} from '@/publico/dominio/formulario';
import { useBuscarClientePublico } from '@/publico/estado/consultas';

/** Error de campo devuelto por el servidor. Lleva el nombre del campo, nunca su valor. */
export interface ErrorDeCampo {
  readonly campo: string;
  readonly mensaje: string;
}

interface PropiedadesFormulario {
  readonly alContinuar: (datos: FormularioReserva) => void;
  readonly errorServidor: ErrorDeCampo | null;
  readonly alVolver: () => void;
}

const CAMPOS_CONOCIDOS = ['cedula', 'nombre', 'apellido', 'telefono'] as const;

type EstadoBusqueda = 'inactiva' | 'buscando' | 'encontrado' | 'no-encontrado' | 'error';

interface DatosAutocompletados {
  readonly nombre: string;
  readonly apellido: string;
  readonly telefono: string;
}

function esCampoConocido(campo: string): campo is (typeof CAMPOS_CONOCIDOS)[number] {
  return (CAMPOS_CONOCIDOS as readonly string[]).includes(campo);
}

export function FormularioCliente({
  alContinuar,
  errorServidor,
  alVolver,
}: PropiedadesFormulario): React.JSX.Element {
  const {
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setError,
    setFocus,
    setValue,
  } = useForm<FormularioReserva>({
    resolver: zodResolver(esquemaFormularioReserva),
    defaultValues: FORMULARIO_VACIO,
    mode: 'onTouched',
  });
  const busqueda = useBuscarClientePublico();
  const [estadoBusqueda, fijarEstadoBusqueda] = useState<EstadoBusqueda>('inactiva');
  const ultimaConsulta = useRef<string | null>(null);
  const secuenciaConsulta = useRef(0);
  const ultimoAutocompletado = useRef<DatosAutocompletados | null>(null);

  function limpiarAutocompletadoAnterior(): void {
    const anterior = ultimoAutocompletado.current;
    setValue('usarDatosRegistrados', false, { shouldDirty: true, shouldValidate: false });
    if (anterior === null) return;

    for (const campo of ['nombre', 'apellido', 'telefono'] as const) {
      if (getValues(campo) === anterior[campo]) {
        setValue(campo, '', { shouldDirty: true, shouldValidate: false });
      }
    }
    ultimoAutocompletado.current = null;
  }

  async function buscarCliente(cedulaCruda: string): Promise<void> {
    const cedula = normalizarCedula(cedulaCruda);
    if (!cedula.ok) {
      fijarEstadoBusqueda('inactiva');
      return;
    }

    const normalizada = valorCedula(cedula.valor);
    if (ultimaConsulta.current === normalizada) return;
    ultimaConsulta.current = normalizada;
    const secuencia = (secuenciaConsulta.current += 1);
    fijarEstadoBusqueda('buscando');

    try {
      const resultado = await busqueda.mutateAsync({ cedula: cedulaCruda });
      const cedulaActual = normalizarCedula(getValues('cedula'));
      if (
        secuencia !== secuenciaConsulta.current ||
        !cedulaActual.ok ||
        valorCedula(cedulaActual.valor) !== normalizada
      ) {
        return;
      }

      if (!resultado.encontrado) {
        limpiarAutocompletadoAnterior();
        fijarEstadoBusqueda('no-encontrado');
        return;
      }

      ultimoAutocompletado.current = resultado.cliente;
      setValue('usarDatosRegistrados', true, { shouldDirty: true, shouldValidate: false });
      setValue('nombre', resultado.cliente.nombre, { shouldDirty: true, shouldValidate: true });
      setValue('apellido', resultado.cliente.apellido, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('telefono', resultado.cliente.telefono, {
        shouldDirty: true,
        shouldValidate: true,
      });
      fijarEstadoBusqueda('encontrado');
    } catch {
      if (secuencia === secuenciaConsulta.current) {
        ultimaConsulta.current = null;
        fijarEstadoBusqueda('error');
      }
    }
  }

  useEffect(() => {
    if (errorServidor === null || !esCampoConocido(errorServidor.campo)) {
      return;
    }
    setError(errorServidor.campo, { type: 'server', message: errorServidor.mensaje });
    setFocus(errorServidor.campo);
  }, [errorServidor, setError, setFocus]);

  const registroCedula = register('cedula');

  return (
    <form
      className="formulario"
      noValidate
      onSubmit={(evento) => {
        void handleSubmit(alContinuar)(evento);
      }}
    >
      <div className="campo">
        <label htmlFor="cedula">Cedula</label>
        <input
          aria-describedby={errors.cedula ? 'cedula-ayuda cedula-error' : 'cedula-ayuda'}
          aria-invalid={errors.cedula ? true : undefined}
          autoComplete="off"
          id="cedula"
          inputMode="numeric"
          maxLength={LARGOS.cedula}
          type="text"
          {...registroCedula}
          onChange={(evento) => {
            void registroCedula.onChange(evento);
            const actual = normalizarCedula(evento.currentTarget.value);
            if (
              ultimaConsulta.current !== null &&
              (!actual.ok || valorCedula(actual.valor) !== ultimaConsulta.current)
            ) {
              secuenciaConsulta.current += 1;
              ultimaConsulta.current = null;
              limpiarAutocompletadoAnterior();
              fijarEstadoBusqueda('inactiva');
            }
          }}
          onBlur={(evento) => {
            void registroCedula.onBlur(evento);
            void buscarCliente(evento.currentTarget.value);
          }}
        />
        <p className="campo__ayuda" id="cedula-ayuda">
          Con o sin puntos y guion. Ejemplo: 1.111.111-1
        </p>
        {errors.cedula ? (
          <p className="campo__error" id="cedula-error">
            {errors.cedula.message}
          </p>
        ) : null}
        {estadoBusqueda === 'buscando' ? (
          <p aria-live="polite" className="campo__ayuda" id="cedula-busqueda">
            Buscando tus datos…
          </p>
        ) : estadoBusqueda === 'encontrado' ? (
          <p aria-live="polite" className="campo__exito" id="cedula-busqueda">
            Encontramos tu ficha. Mostramos tus datos protegidos.
          </p>
        ) : estadoBusqueda === 'no-encontrado' ? (
          <p aria-live="polite" className="campo__ayuda" id="cedula-busqueda">
            Es tu primera reserva. Completa tus datos para registrarte.
          </p>
        ) : estadoBusqueda === 'error' ? (
          <p className="campo__error" id="cedula-busqueda" role="alert">
            No pudimos buscar tus datos. Podes completarlos manualmente.
          </p>
        ) : null}
      </div>

      <div className="campo">
        <label htmlFor="nombre">Nombre</label>
        <input
          aria-describedby={errors.nombre ? 'nombre-error' : undefined}
          aria-invalid={errors.nombre ? true : undefined}
          autoComplete="given-name"
          id="nombre"
          maxLength={LARGOS.nombre}
          readOnly={estadoBusqueda === 'encontrado'}
          type="text"
          {...register('nombre')}
        />
        {errors.nombre ? (
          <p className="campo__error" id="nombre-error">
            {errors.nombre.message}
          </p>
        ) : null}
      </div>

      <div className="campo">
        <label htmlFor="apellido">Apellido</label>
        <input
          aria-describedby={errors.apellido ? 'apellido-error' : undefined}
          aria-invalid={errors.apellido ? true : undefined}
          autoComplete="family-name"
          id="apellido"
          maxLength={LARGOS.apellido}
          readOnly={estadoBusqueda === 'encontrado'}
          type="text"
          {...register('apellido')}
        />
        {errors.apellido ? (
          <p className="campo__error" id="apellido-error">
            {errors.apellido.message}
          </p>
        ) : null}
      </div>

      <div className="campo">
        <label htmlFor="telefono">Telefono</label>
        <input
          aria-describedby={errors.telefono ? 'telefono-ayuda telefono-error' : 'telefono-ayuda'}
          aria-invalid={errors.telefono ? true : undefined}
          autoComplete="tel"
          id="telefono"
          inputMode="tel"
          maxLength={LARGOS.telefono}
          readOnly={estadoBusqueda === 'encontrado'}
          type="tel"
          {...register('telefono')}
        />
        <p className="campo__ayuda" id="telefono-ayuda">
          Celular con WhatsApp. Ejemplo: 099 000 000
        </p>
        {errors.telefono ? (
          <p className="campo__error" id="telefono-error">
            {errors.telefono.message}
          </p>
        ) : null}
      </div>

      <fieldset className="consentimientos">
        <legend>Antes de seguir</legend>

        <div className="consentimiento">
          <input
            aria-describedby={
              errors.aceptaCancelacion
                ? 'cancelacion-detalle cancelacion-error'
                : 'cancelacion-detalle'
            }
            aria-invalid={errors.aceptaCancelacion ? true : undefined}
            id="aceptaCancelacion"
            type="checkbox"
            {...register('aceptaCancelacion')}
          />
          <label htmlFor="aceptaCancelacion">Acepto las condiciones de cancelación</label>
          <p className="campo__ayuda" id="cancelacion-detalle">
            La reserva queda registrada automáticamente al confirmarla. Si luego solicitás la
            cancelación, se cobrará un cargo de $250. Para cancelar, comunicate con el complejo. Si
            el complejo cancela el turno, no se aplicará ningún cargo.
          </p>
          {errors.aceptaCancelacion ? (
            <p className="campo__error" id="cancelacion-error">
              {errors.aceptaCancelacion.message}
            </p>
          ) : null}
        </div>
      </fieldset>

      <div className="acciones">
        <button className="boton boton--secundario" onClick={alVolver} type="button">
          Cambiar horario
        </button>
        <button className="boton boton--primario" type="submit">
          Revisar reserva
        </button>
      </div>
    </form>
  );
}
